import { ObjectOrArray } from '@gov-nx/core/types';
import type {
	CrzResponse,
	Category,
	SimpleCategoryType,
	CategoryWithItems,
	CategoryItem,
	UserDataFromCrz,
} from '@gov-nx/module/service';
import { toDateReadable } from '@gov-nx/utils/common';

// ─── Primitive extractors ────────────────────────────────────────────────────

const getXVal = (
	field: { xVal: string } | null | undefined
): string | undefined => field?.xVal;

const getCodeVal = (
	field: { value: { xVal: string } | null | undefined } | null | undefined
): string | undefined => field?.value?.xVal;

const getDateVal = (
	field: { xVal: string } | null | undefined
): string | undefined => (field?.xVal ? toDateReadable(field.xVal) : undefined);

const getRazeValues = (
	raze: Parameters<typeof ObjectOrArray.array>[0]
): (string | undefined)[] =>
	ObjectOrArray.array(raze).map((r) =>
		getCodeVal(r as { value: { xVal: string } | null | undefined })
	);

const getXValArray = (
	field: Parameters<typeof ObjectOrArray.array>[0]
): (string | undefined)[] =>
	ObjectOrArray.array(field).map((item) =>
		getXVal(item as { xVal: string } | null | undefined)
	);

// ─── Category builders ───────────────────────────────────────────────────────

/**
 * Builds a CategoryWithItems from a nullable CRZ list field.
 * Returns undefined when the source field is absent.
 * Items without an id are filtered out.
 */
const buildListCategory = <
	T extends { id: { xVal: string } | null | undefined },
>(
	source: T | T[] | null | undefined,
	mapItem: (item: T) => Omit<CategoryItem, 'id'> & { id: string | undefined }
): CategoryWithItems | undefined => {
	if (!source) return undefined;
	return {
		type: 'list',
		items: ObjectOrArray.array(source)
			.map(mapItem)
			.filter((item): item is CategoryItem => !!item.id),
	};
};

// ─── Public API ──────────────────────────────────────────────────────────────

export const getUserDataFromCrzData = (
	response: CrzResponse
): UserDataFromCrz | undefined => {
	if (!response.Data[0].r) return;

	const osoba = response.Data[0].r.crzOdpoved.kontextData.osobaDrzitel;

	const dokladDrziteleType =
		osoba.dokladDrziteleType?.druh?.xVal &&
		osoba.dokladDrziteleType?.cislo?.xVal
			? `${osoba.dokladDrziteleType.druh.xVal} ${osoba.dokladDrziteleType.cislo.xVal}`
			: undefined;

	return {
		prijmeni: getXVal(osoba.prijmeni),
		jmeno: getXVal(osoba.jmeno),
		datumNarozeni: getXVal(osoba.datumNarozeni),
		dokladDrziteleType,
		email: getXVal(osoba.email),
		telefon: getXVal(osoba.telefon),
	};
};

type TransformResult =
	| {
			opravneni: { [key: string]: Category | undefined };
			zbraneVyjimkyPovoleni: { [key: string]: Category | undefined };
	  }
	| undefined;

// Functions used to transform CRZ data in XML JSON format to structured records for further usage
// on different variants of Gun List service.
// TODO: Response type will be generate to get rid of type any
export const transformCrzDataToRecordsCompleteGunList = (
	response: CrzResponse | undefined
): TransformResult => {
	if (!response || !response.Data?.[0].r) return;

	const data = response.Data[0].r.crzOdpoved.kontextData;

	// ── Opravneni ────────────────────────────────────────────────────────────

	const zbrojniOpravneni: SimpleCategoryType | undefined = data.zbrojniOpravneni
		?.id
		? {
				type: 'simple',
				druhZbrojnihoOpravneni: getCodeVal(
					data.zbrojniOpravneni.druhZbrojnihoOpravneni
				),
				'datumZapisu.zbrojniOpravneni': getDateVal(
					data.zbrojniOpravneni.datumZapisu
				),
				zdravotniZpusobilostDo: getDateVal(
					data.zbrojniOpravneni.zdravotniZpusobilostDo
				),
				omezeniZdravotniZpusobilosti: getXVal(
					data.zbrojniOpravneni.omezeniZdravotniZpusobilosti
				),
				stavZbrojnihoOpravneni: getCodeVal(
					data.zbrojniOpravneni.stavZbrojnihoOpravneni
				),
				id: data.zbrojniOpravneni.id.xVal,
			}
		: undefined;

	const zbrojniLicence: SimpleCategoryType | undefined = data.zbrojniLicence?.id
		? {
				type: 'simple',
				druhZbrojniLicence: getCodeVal(data.zbrojniLicence.druhZbrojniLicence),
				'datumZapisu.zbrojniLicence': getDateVal(
					data.zbrojniLicence.datumZapisu
				),
				stavZbrojniLicence: getCodeVal(data.zbrojniLicence.stavZbrojniLicence),
				id: data.zbrojniLicence.id.xVal,
			}
		: undefined;

	const zapsanaAutorizace: SimpleCategoryType | undefined = data
		.zapsanaAutorizace?.id
		? {
				type: 'simple',
				'datumZapisu.base': getDateVal(data.zapsanaAutorizace.datumZapisu),
				stavAutorizace: getCodeVal(data.zapsanaAutorizace.stavAutorizace),
				id: data.zapsanaAutorizace.id.xVal,
			}
		: undefined;

	const evropskyZbrojniPas = buildListCategory(
		data.evropskyZbrojniPas,
		(item) => ({
			cisloEzp: getXVal(item.cislo),
			datumVydani: getDateVal(item.datumVydani),
			datumPlatnostiDo: getDateVal(item.datumPlatnostiDo),
			stavEvropskehoZbrojnihoPasu: getCodeVal(item.stavEvropskehoZbrojnihoPasu),
			id: getXVal(item.id),
		})
	);

	const municniOpravneniNakladaniMunice = buildListCategory(
		data.municniOpravneniNakladaniMunice,
		(item) => ({
			druhMunicnihoOpravneni: getCodeVal(item.druhMunicnihoOpravneni),
			'datumZapisu.municniOpravneniNakladaniMunice': getDateVal(
				item.datumZapisu
			),
			stavOpravneni: getCodeVal(item.stavOpravneni),
			zdravotniZpusobilostDo: getDateVal(item.zdravotniZpusobilostDo),
			omezeniZdravotniZpusobilosti: getXVal(item.omezeniZdravotniZpusobilosti),
			id: getXVal(item.id),
		})
	);

	const aprobace = buildListCategory(data.aprobace, (item) => ({
		'datumZapisu.aprobace': getDateVal(item.datumZapisu),
		druhAprobace: getCodeVal(item.druhAprobace),
		id: getXVal(item.id),
	}));

	// ── Zbrane, vyjimky, povoleni ─────────────────────────────────────────────

	const vyjimkyNaZbranKategorieR1 = buildListCategory(
		data.vyjimkyNaZbranKategorieR1,
		(item) => ({
			cisloJednaci: getXVal(item.cisloJednaci),
			'datumZapisu.vyjimka': getDateVal(item.datumZapisu),
			druhZbrane: getCodeVal(item.druhZbrane),
			vyrobce: getCodeVal(item.vyrobce),
			model: getCodeVal(item.model),
			raze: getRazeValues(item.raze),
			vyrobniCislo: getXValArray(item.vyrobniCislo),
			id: getXVal(item.id),
		})
	);

	const vyjimkyNaStrelivoS1 = buildListCategory(
		data.vyjimkyNaStrelivoS1,
		(item) => ({
			cisloJednaci: getXVal(item.cisloJednaci),
			'datumZapisu.vyjimka': getDateVal(item.datumZapisu),
			raze: getRazeValues(item.raze),
			pocetKusu: getXVal(item.pocetKusu),
			zbyvajiciPocetKusu: getXVal(item.zbyvajiciPocetKusu),
			id: getXVal(item.id),
		})
	);

	const vyjimkyNaZbranStrelivoKategorieR2S2 = buildListCategory(
		data.vyjimkyNaZbranStrelivoKategorieR2S2,
		(item) => ({
			cisloJednaci: getXVal(item.cisloJednaci),
			'datumZapisu.vyjimka': getDateVal(item.datumZapisu),
			predmetVyjimky: getCodeVal(item.predmetVyjimky),
			id: getXVal(item.id),
		})
	);

	const vyjimkyNaNadlimitniZasobnik = buildListCategory(
		data.vyjimkyNaNadlimitniZasobnik,
		(item) => ({
			cisloJednaci: getXVal(item.cisloJednaci),
			'datumZapisu.vyjimka': getDateVal(item.datumZapisu),
			id: getXVal(item.id),
		})
	);

	const povoleniNaZbranKategorieR3 = buildListCategory(
		data.povoleniNaZbranKategorieR3,
		(item) => ({
			cisloJednaci: getXVal(item.cisloJednaci),
			'datumZapisu.povoleni': getDateVal(item.datumZapisu),
			id: getXVal(item.id),
		})
	);

	const zbraneHlavnihoDrziteleR1R2R3R4 = buildListCategory(
		data.zbraneHlavnihoDrziteleR1R2R3R4,
		(item) => ({
			vedlejsiDrzitelFO: item.vedlejsiDrzitelFO
				? ObjectOrArray.array(item.vedlejsiDrzitelFO).map((fo) => ({
						prijmeniVedlejsihoDrziteleType: getXVal(
							fo.prijmeniVedlejsihoDrziteleType
						),
						jmenoVedlejsihoDrziteleType: getXVal(
							fo.jmenoVedlejsihoDrziteleType
						),
						datumNarVedlejsihoDrziteleType: getDateVal(
							fo.datumNarVedlejsihoDrziteleType
						),
					}))
				: undefined,
			kategorie: getCodeVal(item.kategorie),
			druhZbrane: getCodeVal(item.druhZbrane),
			popisHlavniCastiZbrane: getXVal(item.popisHlavniCastiZbrane),
			vyrobniCislo: getXValArray(item.vyrobniCislo),
			vyrobce: getCodeVal(item.vyrobce),
			vzor: getCodeVal(item.vzor),
			raze: getRazeValues(item.raze),
			'datumZapisu.registrace': getDateVal(item.datumZapisu),
			id: getXVal(item.id),
		})
	);

	const vedlejsiZbrane = buildListCategory(data.vedlejsiZbrane, (item) => ({
		kategorie: getCodeVal(item.kategorie),
		druhZbrane: getCodeVal(item.druhZbrane),
		popisHlavniCastiZbrane: getXVal(item.popisHlavniCastiZbrane),
		vyrobniCislo: getXValArray(item.vyrobniCislo),
		vyrobce: getCodeVal(item.vyrobce),
		vzor: getCodeVal(item.vzor),
		raze: getRazeValues(item.raze),
		'datumZapisu.do-crz': getDateVal(item.datumZapisu),
		id: getXVal(item.id),
	}));

	const ohlaseneZbraneKategoriePO = buildListCategory(
		data.ohlaseneZbraneKategoriePO,
		(item) => ({
			druhZbrane: getCodeVal(item.druhZbrane),
			vyrobniCislo: getXValArray(item.vyrobniCislo),
			vyrobce: getCodeVal(item.vyrobce),
			vzor: getCodeVal(item.vzor),
			raze: getRazeValues(item.raze),
			'datumZapisu.do-crz': getDateVal(item.datumZapisu),
			id: getXVal(item.id),
		})
	);

	return {
		opravneni: {
			zbrojniOpravneni,
			zbrojniLicence,
			municniOpravneniNakladaniMunice,
			aprobace,
			zapsanaAutorizace,
			evropskyZbrojniPas,
		},
		zbraneVyjimkyPovoleni: {
			zbraneHlavnihoDrziteleR1R2R3R4,
			vedlejsiZbrane,
			ohlaseneZbraneKategoriePO,
			vyjimkyNaZbranKategorieR1,
			vyjimkyNaStrelivoS1,
			vyjimkyNaZbranStrelivoKategorieR2S2,
			vyjimkyNaNadlimitniZasobnik,
			povoleniNaZbranKategorieR3,
		},
	};
};
